// src/contexts/notificationContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
} from "react";
import Notification from "../components/Shared/Notification";

type NotificationType = "success" | "error" | "info";

interface NotificationData {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
}

interface NotificationContextData {
  showNotification: (
    type: NotificationType,
    message: string,
    duration?: number
  ) => void;
}

const NotificationContext = createContext<NotificationContextData | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  // Use a ref to track the last notification ID to ensure uniqueness
  const lastNotificationIdRef = useRef<number>(0);

  const showNotification = (
    type: NotificationType,
    message: string,
    duration = 3000
  ) => {
    // Increment the notification counter to ensure unique IDs
    lastNotificationIdRef.current += 1;
    const id = `notification-${Date.now()}-${lastNotificationIdRef.current}`;

    setNotifications((prev) => [...prev, { id, type, message, duration }]);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {notifications.map((notification) => (
        <Notification
          key={notification.id}
          type={notification.type}
          message={notification.message}
          duration={notification.duration}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextData => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};
